import { useCallback, useEffect, useState } from 'react';

const INSTALLED_KEY = 'shreefxPwaInstalled';

/** Shared deferred prompt — same pattern as Kingdom FX. */
let deferredPromptEvent = null;
const promptListeners = new Set();

function notifyPromptListeners() {
  promptListeners.forEach((fn) => fn(deferredPromptEvent));
}

function ensureInstallListeners() {
  if (typeof window === 'undefined' || window.__shreefxPwaListenersBound) return;
  window.__shreefxPwaListenersBound = true;

  window.addEventListener('beforeinstallprompt', (e) => {
    // Required so Chrome does not show its mini-infobar; we trigger via our button.
    e.preventDefault();
    deferredPromptEvent = e;
    notifyPromptListeners();
  });

  window.addEventListener('appinstalled', () => {
    deferredPromptEvent = null;
    try {
      localStorage.setItem(INSTALLED_KEY, '1');
    } catch (err) {}
    notifyPromptListeners();
  });
}

ensureInstallListeners();

export function isRunningAsInstalledApp() {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia('(display-mode: standalone)').matches) return true;
    if (window.matchMedia('(display-mode: fullscreen)').matches) return true;
    if (window.matchMedia('(display-mode: minimal-ui)').matches) return true;
    if (window.navigator.standalone === true) return true;
  } catch (err) {}
  return false;
}

export function getBrowserKind() {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
  if (isIOS && isSafari) return 'safari-ios';
  if (/firefox/i.test(ua)) return 'firefox';
  if (/edg/i.test(ua) || /chrome|crios|chromium/i.test(ua)) return 'chromium';
  if (isSafari) return 'safari-mac';
  return 'other';
}

export async function registerServiceWorker() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return null;
  }
  try {
    const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    return reg;
  } catch (err) {
    console.warn('SW registration failed', err);
    return null;
  }
}

export function markPwaInstalled() {
  try {
    localStorage.setItem(INSTALLED_KEY, '1');
  } catch (err) {}
}

/**
 * Kingdom FX–style install hook:
 * - capture beforeinstallprompt
 * - Install click → event.prompt() when available
 * - otherwise show manual instructions popup
 */
export function usePwaInstall() {
  const [isInstalled, setIsInstalled] = useState(() => isRunningAsInstalledApp());
  const [canPrompt, setCanPrompt] = useState(() => Boolean(deferredPromptEvent));
  const [installing, setInstalling] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [browserKind] = useState(() => getBrowserKind());

  useEffect(() => {
    ensureInstallListeners();
    registerServiceWorker();

    if (isRunningAsInstalledApp()) {
      markPwaInstalled();
      setIsInstalled(true);
    }

    const onPrompt = (event) => setCanPrompt(Boolean(event));
    promptListeners.add(onPrompt);
    setCanPrompt(Boolean(deferredPromptEvent));

    const onInstalled = () => {
      setIsInstalled(true);
      setCanPrompt(false);
      setShowGuide(false);
    };
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      promptListeners.delete(onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPromptEvent) return { outcome: 'unavailable' };
    setInstalling(true);
    try {
      deferredPromptEvent.prompt();
      const { outcome } = await deferredPromptEvent.userChoice;
      console.log('PWA install outcome:', outcome);
      if (outcome === 'accepted') {
        markPwaInstalled();
        setIsInstalled(true);
      }
      deferredPromptEvent = null;
      notifyPromptListeners();
      return { outcome };
    } catch (err) {
      console.warn('PWA prompt failed', err);
      return { outcome: 'error' };
    } finally {
      setInstalling(false);
    }
  }, []);

  /** Same as Kingdom FX handleInstall */
  const handleInstall = useCallback(async () => {
    if (isInstalled) {
      return { outcome: 'already-installed' };
    }
    if (deferredPromptEvent) {
      return promptInstall();
    }
    setShowGuide(true);
    return { outcome: 'guide' };
  }, [isInstalled, promptInstall]);

  return {
    isInstalled,
    canPrompt,
    canNativeInstall: canPrompt,
    installing,
    showInstallButton: !isInstalled,
    showGuide,
    setShowGuide,
    browserKind,
    promptInstall,
    handleInstall,
  };
}
